import { useState, useEffect } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Badge, Button, Modal, Form, Input, Select,
  InputNumber, DatePicker, Space, Tabs, Checkbox, Progress, Empty, message,
} from 'antd';
import ReactEChartsCore from 'echarts-for-react';
import { vppDemand } from '../../../services/api';

// 事件状态机：published → declared → cleared → executing → assessed，另有 settled / cancelled
const STATUS_MAP = {
  published: { text: '已发布', status: 'processing' },
  declared: { text: '申报中', status: 'warning' },
  cleared: { text: '已出清', status: 'default' },
  executing: { text: '执行中', status: 'warning' },
  assessed: { text: '已考核', status: 'success' },
  settled: { text: '已结算', status: 'success' },
  cancelled: { text: '已取消', status: 'default' },
};

const TYPE_COLORS = { 削峰: 'red', 填谷: 'green', 紧急响应: 'purple' };

const DECL_STATUS = {
  pending: { text: '待出清', color: 'gold' },
  accepted: { text: '已中标', color: 'green' },
  rejected: { text: '未中标', color: 'red' },
};

const fmtTime = (t) => (t ? new Date(t).toLocaleString('zh-CN') : '-');

// TEXT 列存的 JSON 安全解析，失败返回默认值
const parseJson = (s, fallback) => {
  if (!s) return fallback;
  try { return JSON.parse(s); } catch { return fallback; }
};

// 当前用户角色：admin 才有写操作按钮，operator/admin 可申报
const user = JSON.parse(localStorage.getItem('qhy_user') || '{}');
const isAdmin = user.role === 'admin';
const canDeclare = ['admin', 'operator'].includes(user.role);

export default function VppDemand() {
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]); // 供下拉选择与考核记录映射用
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [createOpen, setCreateOpen] = useState(false);
  const [clearEvent, setClearEvent] = useState(null); // 出清弹窗的事件
  const [clearDecls, setClearDecls] = useState([]);
  const [clearChecked, setClearChecked] = useState([]);
  const [clearLoading, setClearLoading] = useState(false);
  const [declEvent, setDeclEvent] = useState(null); // 申报列表弹窗的事件
  const [declList, setDeclList] = useState([]);
  const [declareEventId, setDeclareEventId] = useState(null); // 申报管理页选中的事件
  const [myDecls, setMyDecls] = useState([]);
  const [baselines, setBaselines] = useState([]);
  const [computeLoading, setComputeLoading] = useState(false);
  const [monitorEventId, setMonitorEventId] = useState(null);
  const [monitorData, setMonitorData] = useState(null);
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [assessDetail, setAssessDetail] = useState(null); // 考核详情弹窗（基线 vs 实际 + 样本明细）
  const [activeTab, setActiveTab] = useState('events');
  const [createForm] = Form.useForm();
  const [declareForm] = Form.useForm();

  // 事件列表（分页）
  const loadEvents = async (p = page, ps = pageSize) => {
    setEventsLoading(true);
    try {
      const res = await vppDemand.list({ page: p, pageSize: ps });
      setEvents(res.data || []);
      setEventsTotal(res.total || 0);
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setEventsLoading(false);
    }
  };

  // 全量事件（下拉选项、考核记录映射）
  const loadAllEvents = async () => {
    try {
      const res = await vppDemand.list({ page: 1, pageSize: 1000 });
      setAllEvents(res.data || []);
    } catch (err) {
      // 拦截器已统一提示
    }
  };

  const loadSummary = async () => {
    try {
      const res = await vppDemand.revenueSummary();
      setSummary(res.data);
    } catch (err) {
      // 拦截器已统一提示
    }
  };

  const loadBaselines = async () => {
    try {
      const res = await vppDemand.baselines();
      setBaselines(res.data || []);
    } catch (err) {
      // 拦截器已统一提示
    }
  };

  const loadAssessments = async () => {
    try {
      const res = await vppDemand.assessments();
      setAssessments(res.data || []);
    } catch (err) {
      // 拦截器已统一提示
    }
  };

  useEffect(() => {
    loadSummary();
    loadAllEvents();
    loadEvents(1, 10);
  }, []);

  // 切换 Tab 时按需加载数据
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'baselines') loadBaselines();
    if (key === 'assessments') loadAssessments();
  };

  // ============ 事件管理 ============

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      const [start, end] = values.timeRange;
      await vppDemand.create({
        title: values.title,
        type: values.type,
        start_time: start.format('YYYY-MM-DD HH:mm:ss'),
        end_time: end.format('YYYY-MM-DD HH:mm:ss'),
        target_power_kw: values.target_power_kw,
        compensation_price: values.compensation_price,
        description: values.description,
      });
      message.success('事件已创建');
      setCreateOpen(false);
      createForm.resetFields();
      loadEvents();
      loadAllEvents();
      loadSummary();
    } catch (err) {
      // 表单校验失败或拦截器已提示
    }
  };

  // 打开出清弹窗：拉取该事件全部申报，默认勾选待出清的申报
  const openClear = async (record) => {
    setClearEvent(record);
    try {
      const res = await vppDemand.declarations(record.id);
      const decls = res.data || [];
      setClearDecls(decls);
      setClearChecked(decls.filter((d) => d.status === 'pending').map((d) => d.id));
    } catch (err) {
      setClearDecls([]);
    }
  };

  const handleClear = async () => {
    setClearLoading(true);
    try {
      await vppDemand.clear(clearEvent.id, clearChecked);
      message.success('出清完成');
      setClearEvent(null);
      loadEvents();
      loadAllEvents();
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setClearLoading(false);
    }
  };

  // 事件流转操作（开始执行 / 考核 / 取消）
  const handleAction = async (action, record, okText) => {
    try {
      await vppDemand[action](record.id);
      message.success(okText);
      loadEvents();
      loadAllEvents();
      if (action === 'assess') {
        loadSummary();
        if (activeTab === 'assessments') loadAssessments();
      }
    } catch (err) {
      // 拦截器已统一提示（如考核缺遥测数据时后端返回的中文 message）
    }
  };

  // 查看申报列表弹窗
  const openDeclList = async (record) => {
    setDeclEvent(record);
    try {
      const res = await vppDemand.declarations(record.id);
      setDeclList(res.data || []);
    } catch (err) {
      setDeclList([]);
    }
  };

  // 跳转到响应监控 Tab 并选中事件
  const gotoMonitor = (record) => {
    setMonitorEventId(record.id);
    handleTabChange('monitor');
    loadMonitoring(record.id);
  };

  // ============ 申报管理 ============

  const loadEventDecls = async (eventId) => {
    try {
      const res = await vppDemand.declarations(eventId);
      setMyDecls(res.data || []);
    } catch (err) {
      setMyDecls([]);
    }
  };

  const handleDeclare = async () => {
    try {
      const values = await declareForm.validateFields();
      await vppDemand.declare(declareEventId, {
        declared_power_kw: values.declared_power_kw,
        note: values.note,
      });
      message.success('申报已提交');
      declareForm.resetFields();
      loadEventDecls(declareEventId);
      loadEvents();
      loadAllEvents();
    } catch (err) {
      // 表单校验失败或拦截器已提示
    }
  };

  // ============ 基线管理 ============

  const handleComputeBaselines = async () => {
    setComputeLoading(true);
    try {
      const res = await vppDemand.computeBaselines();
      message.success(res.message || '基线计算完成');
      loadBaselines();
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setComputeLoading(false);
    }
  };

  // ============ 响应监控 ============

  const loadMonitoring = async (eventId) => {
    setMonitorLoading(true);
    try {
      const res = await vppDemand.monitoring(eventId);
      setMonitorData(res.data);
    } catch (err) {
      setMonitorData(null);
    } finally {
      setMonitorLoading(false);
    }
  };

  // 基线功率（虚线常量）vs 实际功率曲线
  const monitorOption = () => {
    const series = monitorData?.series || [];
    const times = series.map((p) => p.time || fmtTime(p.ts));
    const lines = [{
      name: '实际功率',
      type: 'line',
      data: series.map((p) => p.power),
      itemStyle: { color: '#1890ff' },
      showSymbol: false,
    }];
    if (monitorData?.baseline != null) {
      lines.push({
        name: '基线功率',
        type: 'line',
        data: series.map(() => monitorData.baseline),
        lineStyle: { type: 'dashed', color: '#fa8c16' },
        itemStyle: { color: '#fa8c16' },
        showSymbol: false,
      });
    }
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: lines.map((l) => l.name) },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: times },
      yAxis: { type: 'value', name: 'kW' },
      series: lines,
    };
  };

  // ============ 表格列定义 ============

  const eventColumns = [
    { title: '事件编号', dataIndex: 'event_no' },
    { title: '标题', dataIndex: 'title' },
    {
      title: '类型', dataIndex: 'type',
      render: (t) => <Tag color={TYPE_COLORS[t]}>{t}</Tag>,
    },
    {
      title: '响应时段', key: 'time',
      render: (_, r) => `${fmtTime(r.start_time)} ~ ${fmtTime(r.end_time)}`,
    },
    { title: '目标功率(kW)', dataIndex: 'target_power_kw' },
    {
      title: '补偿价(元/kWh)', dataIndex: 'compensation_price',
      render: (v) => (v != null ? v : '-'),
    },
    {
      title: '状态', dataIndex: 'status',
      render: (s) => <Badge status={STATUS_MAP[s]?.status || 'default'} text={STATUS_MAP[s]?.text || s} />,
    },
    {
      title: '操作', key: 'action', width: 260,
      render: (_, record) => (
        <Space wrap>
          {isAdmin && ['published', 'declared'].includes(record.status) && (
            <Button type="link" size="small" onClick={() => openClear(record)}>出清</Button>
          )}
          {isAdmin && ['published', 'declared'].includes(record.status) && (
            <Button type="link" size="small" danger onClick={() => handleAction('cancel', record, '事件已取消')}>取消</Button>
          )}
          {isAdmin && record.status === 'cleared' && (
            <Button type="link" size="small" onClick={() => handleAction('start', record, '事件已开始执行')}>开始执行</Button>
          )}
          {isAdmin && record.status === 'cleared' && (
            <Button type="link" size="small" danger onClick={() => handleAction('cancel', record, '事件已取消')}>取消</Button>
          )}
          {isAdmin && record.status === 'executing' && (
            <Button type="link" size="small" onClick={() => handleAction('assess', record, '考核评估完成')}>考核</Button>
          )}
          {record.status === 'executing' && (
            <Button type="link" size="small" onClick={() => gotoMonitor(record)}>监控</Button>
          )}
          <Button type="link" size="small" onClick={() => openDeclList(record)}>申报列表</Button>
        </Space>
      ),
    },
  ];

  const declColumns = [
    { title: '申报用户', dataIndex: 'username' },
    { title: '申报容量(kW)', dataIndex: 'declared_power_kw' },
    {
      title: '状态', dataIndex: 'status',
      render: (s) => <Tag color={DECL_STATUS[s]?.color}>{DECL_STATUS[s]?.text || s}</Tag>,
    },
    { title: '备注', dataIndex: 'note', render: (v) => v || '-' },
    { title: '申报时间', dataIndex: 'created_at', render: fmtTime },
  ];

  const baselineColumns = [
    { title: '表计', dataIndex: 'meter_name' },
    { title: '表计ID', dataIndex: 'meter_id' },
    { title: '基线功率(kW)', dataIndex: 'baseline_power_kw' },
    { title: '计算方法', dataIndex: 'method' },
    { title: '采样天数', dataIndex: 'sample_days' },
    {
      title: '数据质量', dataIndex: 'data_source', width: 100,
      render: (v) => (v === 'insufficient_samples'
        ? <Tag color="orange">样本不足</Tag>
        : <Tag color="green">正常</Tag>),
    },
    {
      title: '样本日明细', dataIndex: 'sample_detail',
      render: (v) => {
        const detail = parseJson(v, []);
        if (!detail.length) return '-';
        return (
          <Space wrap size={4}>
            {detail.map((s) => <Tag key={s.date}>{s.date} {s.avg}kW</Tag>)}
          </Space>
        );
      },
    },
    { title: '计算时间', dataIndex: 'computed_at', render: fmtTime },
  ];

  const eventNoOf = (eventId) => allEvents.find((e) => e.id === eventId)?.event_no || `#${eventId}`;

  const assessmentColumns = [
    { title: '事件', dataIndex: 'event_id', render: (id) => eventNoOf(id) },
    { title: '基线均值(kW)', dataIndex: 'baseline_avg_kw' },
    { title: '实际均值(kW)', dataIndex: 'actual_avg_kw' },
    { title: '响应功率(kW)', dataIndex: 'response_kw' },
    {
      title: '准确率', dataIndex: 'accuracy_rate',
      render: (v) => <Progress percent={v} size="small" style={{ width: 140 }} />,
    },
    {
      title: '结果', dataIndex: 'result',
      render: (r) => <Tag color={r === '达标' ? 'green' : 'red'}>{r}</Tag>,
    },
    {
      title: '数据来源', dataIndex: 'data_source', width: 110,
      render: (v) => {
        if (v === 'ok') return <Tag color="green">正常</Tag>;
        if (v === 'insufficient_samples') return <Tag color="orange">样本不足</Tag>;
        if (v === 'meter_baseline_fallback') return <Tag color="blue">表计基线</Tag>;
        return '-';
      },
    },
    { title: '补贴金额(元)', dataIndex: 'subsidy_amount' },
    { title: '考核时间', dataIndex: 'assessed_at', render: fmtTime },
    {
      title: '操作', key: 'action', width: 100,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => setAssessDetail(record)}>对比曲线</Button>
      ),
    },
  ];

  // 考核详情：基线 vs 实际逐时对比曲线
  const assessChartOption = () => {
    const baselineCurve = parseJson(assessDetail?.baseline_curve, []);
    const actualCurve = parseJson(assessDetail?.actual_curve, []);
    const hours = Array.from(new Set([...baselineCurve, ...actualCurve].map((p) => p.time))).sort();
    const valOf = (curve, t) => curve.find((p) => p.time === t)?.kw ?? null;
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['基线功率', '实际功率'] },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: hours },
      yAxis: { type: 'value', name: 'kW' },
      series: [
        {
          name: '基线功率', type: 'line',
          data: hours.map((t) => valOf(baselineCurve, t)),
          lineStyle: { type: 'dashed', color: '#fa8c16' },
          itemStyle: { color: '#fa8c16' },
        },
        {
          name: '实际功率', type: 'line',
          data: hours.map((t) => valOf(actualCurve, t)),
          itemStyle: { color: '#1890ff' },
        },
      ],
    };
  };

  // 考核详情：基线样本日明细（可溯源）
  const assessSampleColumns = [
    { title: '样本日', dataIndex: 'date' },
    { title: '日均功率(kW)', dataIndex: 'avg_kw' },
    {
      title: '逐时曲线', dataIndex: 'curve',
      render: (curve) => (curve || []).map((p) => `${p.time} ${p.kw}kW`).join('，'),
    },
  ];

  // ============ 各 Tab 内容 ============

  const eventsTab = (
    <Card
      title="事件管理"
      size="small"
      extra={isAdmin && <Button type="primary" onClick={() => setCreateOpen(true)}>新建事件</Button>}
    >
      <Table
        loading={eventsLoading}
        columns={eventColumns}
        dataSource={events.map((e) => ({ ...e, key: e.id }))}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total: eventsTotal,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); loadEvents(p, ps); },
        }}
      />
    </Card>
  );

  const declareTab = (
    <Card title="申报管理" size="small">
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="选择响应事件"
          style={{ width: 280 }}
          value={declareEventId}
          onChange={(v) => { setDeclareEventId(v); loadEventDecls(v); }}
          options={allEvents.map((e) => ({
            value: e.id,
            label: `${e.event_no} ${e.title}（${STATUS_MAP[e.status]?.text || e.status}）`,
          }))}
        />
      </Space>
      {canDeclare && declareEventId && (
        <Form form={declareForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item
            name="declared_power_kw" label="申报容量(kW)"
            rules={[{ required: true, message: '请输入申报容量' }]}
          >
            <InputNumber min={0.01} style={{ width: 160 }} placeholder="kW" />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input style={{ width: 220 }} placeholder="选填" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleDeclare}>提交申报</Button>
          </Form.Item>
        </Form>
      )}
      <Table
        columns={declColumns}
        dataSource={myDecls.map((d) => ({ ...d, key: d.id }))}
        size="small"
        pagination={false}
        locale={{ emptyText: declareEventId ? '暂无申报记录' : '请先选择事件' }}
      />
    </Card>
  );

  const baselinesTab = (
    <Card
      title="基线管理"
      size="small"
      extra={isAdmin && (
        <Button type="primary" loading={computeLoading} onClick={handleComputeBaselines}>计算基线</Button>
      )}
    >
      <Table
        columns={baselineColumns}
        dataSource={baselines.map((b) => ({ ...b, key: b.meter_id }))}
        size="small"
        pagination={false}
      />
    </Card>
  );

  const monitorTab = (
    <Card title="响应监控" size="small">
      <Select
        placeholder="选择执行中的事件"
        style={{ width: 320, marginBottom: 16 }}
        value={monitorEventId}
        loading={monitorLoading}
        onChange={(v) => { setMonitorEventId(v); loadMonitoring(v); }}
        options={allEvents.map((e) => ({
          value: e.id,
          label: `${e.event_no} ${e.title}（${STATUS_MAP[e.status]?.text || e.status}）`,
        }))}
      />
      {monitorData && monitorData.series?.length > 0 ? (
        <ReactEChartsCore option={monitorOption()} style={{ height: 360 }} notMerge />
      ) : (
        <Empty
          description={
            monitorEventId
              ? `该事件暂无监控数据${monitorData?.data_source === 'no_data' ? '（事件时段内无遥测数据）' : ''}`
              : '请选择事件查看基线与实际功率曲线'
          }
        />
      )}
    </Card>
  );

  const assessmentsTab = (
    <Card title="评估与收益" size="small">
      <Table
        columns={assessmentColumns}
        dataSource={assessments.map((a) => ({ ...a, key: a.event_id }))}
        size="small"
        pagination={false}
      />
    </Card>
  );

  return (
    <div>
      {/* 顶部收益概览 */}
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card size="small"><Statistic title="总事件数" value={summary?.total_events ?? 0} suffix="次" /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="已考核" value={summary?.assessed_events ?? 0} suffix="次" /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="达标率" value={summary?.pass_rate ?? 0} suffix="%" valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="总补贴" value={summary?.total_subsidy ?? 0} suffix="元" precision={2} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
      </Row>

      <Tabs
        style={{ marginTop: 16 }}
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          { key: 'events', label: '事件管理', children: eventsTab },
          { key: 'declare', label: '申报管理', children: declareTab },
          { key: 'baselines', label: '基线管理', children: baselinesTab },
          { key: 'monitor', label: '响应监控', children: monitorTab },
          { key: 'assessments', label: '评估与收益', children: assessmentsTab },
        ]}
      />

      {/* 新建事件 */}
      <Modal
        title="新建需求响应事件"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        okText="创建"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item name="title" label="事件标题" rules={[{ required: true, message: '请输入事件标题' }]}>
            <Input placeholder="如：午间削峰响应" />
          </Form.Item>
          <Form.Item name="type" label="事件类型" initialValue="削峰" rules={[{ required: true }]}>
            <Select
              options={[
                { value: '削峰', label: '削峰' },
                { value: '填谷', label: '填谷' },
                { value: '紧急响应', label: '紧急响应' },
              ]}
            />
          </Form.Item>
          <Form.Item name="timeRange" label="响应时段" rules={[{ required: true, message: '请选择响应时段' }]}>
            <DatePicker.RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="target_power_kw" label="目标响应功率(kW)"
            rules={[{ required: true, message: '请输入目标响应功率' }]}
          >
            <InputNumber min={0.01} style={{ width: '100%' }} placeholder="kW" />
          </Form.Item>
          <Form.Item name="compensation_price" label="补偿价格(元/kWh)">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="选填" />
          </Form.Item>
          <Form.Item name="description" label="事件说明">
            <Input.TextArea rows={3} placeholder="选填" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 出清：勾选中标申报 */}
      <Modal
        title={`出清：${clearEvent?.event_no || ''} ${clearEvent?.title || ''}`}
        open={!!clearEvent}
        onCancel={() => setClearEvent(null)}
        onOk={handleClear}
        okText="确认出清"
        confirmLoading={clearLoading}
      >
        {clearDecls.length > 0 ? (
          <>
            <p style={{ color: '#888' }}>勾选中标申报，其余待出清申报将标记为未中标：</p>
            <Checkbox.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              value={clearChecked}
              onChange={setClearChecked}
              options={clearDecls.map((d) => ({
                value: d.id,
                label: `${d.username} 申报 ${d.declared_power_kw} kW${d.note ? `（${d.note}）` : ''} - ${DECL_STATUS[d.status]?.text || d.status}`,
                disabled: d.status !== 'pending',
              }))}
            />
          </>
        ) : (
          <Empty description="该事件暂无申报记录" />
        )}
      </Modal>

      {/* 申报列表 */}
      <Modal
        title={`申报列表：${declEvent?.event_no || ''} ${declEvent?.title || ''}`}
        open={!!declEvent}
        onCancel={() => setDeclEvent(null)}
        footer={null}
        width={720}
      >
        <Table
          columns={declColumns}
          dataSource={declList.map((d) => ({ ...d, key: d.id }))}
          size="small"
          pagination={false}
        />
      </Modal>

      {/* 考核详情：基线 vs 实际对比曲线 + 样本日明细 */}
      <Modal
        title={`考核详情：${assessDetail ? eventNoOf(assessDetail.event_id) : ''}`}
        open={!!assessDetail}
        onCancel={() => setAssessDetail(null)}
        footer={null}
        width={860}
      >
        {assessDetail && (() => {
          const sampleDetail = parseJson(assessDetail.sample_detail, {});
          const samples = sampleDetail.samples || [];
          const excluded = sampleDetail.excluded_days || [];
          const hasCurve = parseJson(assessDetail.baseline_curve, []).length > 0
            || parseJson(assessDetail.actual_curve, []).length > 0;
          return (
            <>
              <p style={{ color: '#888' }}>
                基线口径：事件日前 {sampleDetail.n ?? '-'} 个正常日同时段均值
                {sampleDetail.adjust_factor != null && sampleDetail.adjust_factor !== 1 ? ` × 调整系数 ${sampleDetail.adjust_factor}` : ''}
                {assessDetail.data_source === 'insufficient_samples' && '（样本不足，按实际样本数计算）'}
                {assessDetail.data_source === 'meter_baseline_fallback' && '（历史样本缺失，回退为表计基线之和）'}
              </p>
              {hasCurve ? (
                <ReactEChartsCore option={assessChartOption()} style={{ height: 320 }} notMerge />
              ) : (
                <Empty description="无逐时曲线数据（考核时使用了表计基线回退口径）" />
              )}
              {samples.length > 0 && (
                <>
                  <h4 style={{ margin: '16px 0 8px' }}>样本日明细</h4>
                  <Table
                    columns={assessSampleColumns}
                    dataSource={samples.map((s) => ({ ...s, key: s.date }))}
                    size="small"
                    pagination={false}
                  />
                </>
              )}
              {excluded.length > 0 && (
                <>
                  <h4 style={{ margin: '16px 0 8px' }}>剔除日</h4>
                  <Space wrap size={4}>
                    {excluded.map((e) => <Tag key={e.date} color="orange">{e.date}（{e.reason}）</Tag>)}
                  </Space>
                </>
              )}
            </>
          );
        })()}
      </Modal>
    </div>
  );
}
